import { Box, Card, CardContent, Chip, Grid, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import Chart from "react-apexcharts";
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import { IARDashboardSales, IDashboardAgingSlice, IDashboardDSOTrendSlice, IDashboardInvoicesSlice, IDashboardSalesSlice } from "../../utils/ARDashboard.types";
import { Utils } from "../../utils/common-utils";
import { ApiStatus } from "../../utils/api.types";
import { parseInt } from "lodash";


const Charts = (props: {
  userType?: String,
  salesData?: IDashboardSalesSlice,
  invoicesData?: IDashboardInvoicesSlice,
  agingData?: IDashboardAgingSlice,
  DSOTrendData?: IDashboardDSOTrendSlice
}) => {

  const [salesOptions, setOptions] = useState<any>(null);
  const [salesSeries, setSeries] = useState<any>(null);

  const [invoiceOptions, setInvoiceOptions] = useState<any>(null);
  const [invoicesSeries, setInvoiceSeries] = useState<any>(null);

  const [DSOTrendOptions, setDSOTrendOptions] = useState<any>(null);
  const [DSOTrendSeries, setDSOTrendSeries] = useState<any>(null);

  const [agingOptions, setAgingOptions] = useState<any>(null);
  const [agingSeries, setAgingSeries] = useState<any>(null);

  const { salesData, invoicesData, agingData, DSOTrendData } = props;

  useEffect(() => {
    if (salesData?.status === ApiStatus.SUCCESS) {
      setOptions({
        chart: {
          id: 'apex-charts',
          toolbar: {
            show: false,
          },
        },
        dataLabels: {
          enabled: false
        },
        xaxis: {
          show: false,
          categories: salesData?.dashboardSales.series ? Object.keys(salesData?.dashboardSales.series) : [],
          labels: {
            show: false,
          },
        },
        yaxis: {
          show: true,
          labels: {
            show: true,
          },
        },
        fill: {
          colors: ['#f00']
        },
        tooltip: {
          x: {
            formatter: (val: any) => {
              return val;
            }
          },
          y: {
            formatter: (val: any) => {
              return val;
            }
          }
        }
      });

      setSeries([{
        name: 'Sales',
        data: salesData?.dashboardSales.series ? Object.values(salesData.dashboardSales.series) : [],
      }]);
    }

  }, [salesData]);

  useEffect(() => {
    if (invoicesData?.status === ApiStatus.SUCCESS) {
      setInvoiceOptions({
        chart: {
          id: 'apex-charts',
          toolbar: {
            show: false,
          },
        },
        dataLabels: {
          enabled: false
        },
        xaxis: {
          show: false,
          categories: invoicesData?.dashboardInvoices.series ? Object.keys(invoicesData?.dashboardInvoices.series) : [],
          labels: {
            show: false,
          },
        },
        yaxis: {
          show: true,
          labels: {
            show: true,
          },
        },
        fill: {
          colors: ['#FFA500']
        },
        tooltip: {
          x: {
            formatter: (val: any) => {
              return val;
            }
          },
          y: {
            formatter: (val: any) => {
              return val;
            }
          }
        }
      });

      setInvoiceSeries([{
        name: 'Invoice Volume',
        data: invoicesData?.dashboardInvoices.series ? Object.values(invoicesData.dashboardInvoices.series) : [],
      }]);
    }

  }, [invoicesData]);

  useEffect(() => {
  }, [agingData]);

  useEffect(() => {
    if (DSOTrendData?.status === ApiStatus.SUCCESS) {
      setDSOTrendOptions({
        chart: {
          id: 'apex-charts',
          toolbar: {
            show: false,
          },
        },
        dataLabels: {
          enabled: false
        },
        xaxis: {
          show: false,
          categories: DSOTrendData?.dashboardDSOTrend.series ? Object.keys(DSOTrendData?.dashboardDSOTrend.series) : [],
          labels: {
            show: false,
          },
        },
        yaxis: {
          show: false,
          labels: {
            show: false,
          },
        },
        fill: {
          colors: ['#8B8000']
        },
      });


      setDSOTrendSeries([{
        name: 'DSO Trend',
        data: DSOTrendData?.dashboardDSOTrend?.series ? Object.values(DSOTrendData?.dashboardDSOTrend?.series) : [],
      }]);
    }

  }, [DSOTrendData]);

  useEffect(() => {
    if (agingData?.status === ApiStatus.SUCCESS) {
      setAgingOptions({
        chart: {
          type: 'donut',
        },
        labels: Object.keys(agingData.dashboardAging.series),
        responsive: [{
          breakpoint: 480,
          options: {
            chart: {
              width: 200
            },
            legend: {
              position: 'bottom'
            }
          }
        }]
      });
      setAgingSeries(Object.values(agingData.dashboardAging.series))
    }

  }, [agingData])

  return (
    <>
      <Box sx={{ flexGrow: 1, mb: '50px' }}>

        <Grid container sx={{ mb: '10px' }} spacing={{ xs: 1, md: 1 }}>

          <Grid item xs={12} sm={6} md={6} lg={6} xl={3}>
            <Card sx={{ mr: '10px', pt: '30px' }} className='chart-card'>
              {salesOptions && <>
                <Box sx={{ pl: '20px' }}>
                  <Typography sx={{ color: 'gray', mb: '10px' }}>{props.userType === 'AR'? 'Sales': 'Purchases'}</Typography>
                  <Typography component='h1' sx={{ fontWeight: 'bold', display: 'inline-block', mr: '40px' }}>
                    {Utils.formatNumber(Number(salesData?.dashboardSales.salesValue), true)}
                  </Typography>
                  <Chip
                    label={Utils.formatNumber(Number(salesData?.dashboardSales.trend.value), false) + '%'}
                    icon={salesData?.dashboardSales.trend.hasGrown ? <TrendingUpIcon /> : <TrendingDownIcon />}
                    variant="outlined"
                    sx={{
                      p: '10px',
                      backgroundColor: 'rgb(255,251,230)',
                      borderColor: 'rgb(255,214,102)',
                      color: 'rgb(250,173,20)',
                      borderRadius: '4px'
                    }}
                  />
                </Box>
                <Chart options={salesOptions} series={salesSeries} type="bar" height={200} />
              </>}
            </Card>
          </Grid>



          <Grid item xs={12} sm={6} md={6} lg={6} xl={3}>
            <Card sx={{ pt: '30px' }} className='chart-card'>
              {invoicesSeries && <>
                <Box sx={{ pl: '20px' }}>
                  <Typography sx={{ color: 'gray', mb: '10px' }}>Invoice Volume</Typography>
                  <Typography component='h1' sx={{ fontWeight: 'bold', display: 'inline-block', mr: '40px' }}>
                    {Utils.formatNumber(Number(invoicesData?.dashboardInvoices.invoiceValue), false) + ' invoices'}
                  </Typography>
                  <Chip
                    label={Utils.formatNumber(Number(invoicesData?.dashboardInvoices.trend.value), false) + '%'}
                    icon={invoicesData?.dashboardInvoices.trend.hasGrown ? <TrendingUpIcon /> : <TrendingDownIcon />}
                    variant="outlined"
                    sx={{
                      p: '10px', backgroundColor: 'rgb(255,251,230)',
                      borderColor: 'rgb(255,214,102)', color: 'rgb(250,173,20)',
                      borderRadius: '4px'
                    }}
                  />
                </Box>
                <Chart options={invoiceOptions} series={invoicesSeries} type="bar" height={200} />
              </>}
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={6} lg={6} xl={3}>
            <Card sx={{ mr: '10px', pt: '30px', pb: '12px' }} className='chart-card' style={{height: '100%'}}>
              {agingSeries && <>
                <Box sx={{ pl: '20px' }}>
                  <Typography sx={{ color: 'gray', mb: '10px' }}>Ageing</Typography>
                  {/* <Typography component='h1' sx={{ fontWeight: 'bold', display: 'inline-block', mr: '40px' }}>{Utils.formatNumber(agingData?.dashboardAging.agingValue, true)}</Typography> */}
                  {/* <Chip
                    label="24%"
                    icon={<TrendingDownIcon />}
                    variant="outlined"
                    sx={{
                      p: '10px', backgroundColor: 'rgb(230,247,255)',
                      borderColor: 'rgb(105,192,255)', color: 'rgb(24,144,255)',
                      borderRadius: '4px'
                    }}
                  /> */}
                </Box>
                <Chart options={agingOptions} series={agingSeries} type="donut" height={200} />
              </>}
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={6} lg={6} xl={3}>
            <Card className='chart-card' style={{height: '100%'}}>
              {DSOTrendSeries && <>
                <Box sx={{ pl: '20px', pt: '30px'}}>
                  <Typography sx={{ color: 'gray', mb: '10px' }}>{props.userType==='AR'? 'DSO': 'DPO'} Trend</Typography>
                  {/* <Typography component='h1' sx={{ fontWeight: 'bold', display: 'inline-block', mr: '40px' }}>$35,000</Typography> */}
                  {/* <Chip
                    label="24%"
                    icon={<TrendingUpIcon />}
                    variant="outlined"
                    sx={{
                      p: '10px', backgroundColor: 'rgb(230,247,255)',
                      borderColor: 'rgb(105,192,255)', color: 'rgb(24,144,255)',
                      borderRadius: '4px'
                    }}
                  /> */}
                </Box>
                <Chart options={DSOTrendOptions} series={DSOTrendSeries} type="line" height={200} />
              </>}
            </Card>
          </Grid>

        </Grid>
      </Box>
    </>
  );
};

export default Charts;
